import { type McpServerInfo } from '@myre/app-agent-generated/types/McpServerInfo';
import { type UiLibI18n } from '@myre/domain-i18n-wordings';
import {
  ButtonComponent,
  ButtonSubTheme,
  ButtonTheme,
  ButtonType,
} from '@myre/ui-primitive-components';
import { DropdownComponent, type ISelectItem } from '@myre/ui-primitive-forms';

import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { WORDINGS } from '../../../myre-wordings-loader';

import { TrimOnPasteDirective } from '../../directives/trim-on-paste.directive';
import {
  type ChatInputConfig,
  type ChatStatus,
} from '../../interfaces/chat.interfaces';

const DEFAULT_PLACEHOLDER = '';

@Component({
  selector: 'app-chat-input',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    ButtonComponent,
    TrimOnPasteDirective,
    DropdownComponent,
  ],
  templateUrl: './chat-input.component.html',
  styleUrl: './chat-input.component.scss',
})
export class ChatInputComponent {
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _wordings = inject<UiLibI18n>(WORDINGS);

  protected readonly ButtonTheme = ButtonTheme;
  protected readonly ButtonSubTheme = ButtonSubTheme;
  protected readonly ButtonType = ButtonType;

  protected get chatWordings() {
    return this._wordings._library.chat;
  }

  @ViewChild('textareaInput')
  private readonly _textareaInput?: ElementRef<HTMLTextAreaElement>;

  public readonly chatStatus = input.required<ChatStatus>();
  public readonly config = input<ChatInputConfig>({});
  public readonly disabled = input(false);

  /** Agent selector items */
  public readonly agentItems = input<ISelectItem<string>[]>([]);

  /** Currently selected agent name */
  public readonly selectedAgentName = input('');

  public readonly messageSent = output<string>();
  public readonly stopRequested = output<void>();
  public readonly filesSelected = output<File[]>();
  public readonly enabledToolsChange = output<string[]>();
  public readonly debugChange = output<boolean>();

  /** Emitted when the user picks a different agent */
  public readonly agentChange = output<string>();

  /** Emitted when the user asks to see or edit what MYRE remembers about them. */
  public readonly memoryRequested = output<void>();

  protected readonly form = this._formBuilder.group({
    userInput: ['', Validators.required],
  });

  protected readonly selectedFiles = signal<File[]>([]);

  /** True while a file is dragged over the input — drives the drop-zone styling. */
  protected readonly _dragOver = signal(false);

  protected readonly placeholder = computed(
    () => this.config().placeholder ?? DEFAULT_PLACEHOLDER,
  );

  protected readonly enableUpload = computed(
    () => this.config().enableUpload ?? true,
  );

  protected readonly acceptedFileTypes = computed(
    () => this.config().acceptedFileTypes ?? '*',
  );

  protected readonly isAdmin = computed(() => this.config().isAdmin ?? false);

  protected readonly showAgentSelector = computed(
    () => this.agentItems().length > 1,
  );

  protected readonly debugEnabled = signal(false);

  protected readonly mcpServers = computed<readonly McpServerInfo[]>(
    () => this.config().mcpServers ?? [],
  );

  protected readonly hasTools = computed(() => this.mcpServers().length > 0);

  protected readonly showToolsModal = signal(false);

  /** The settings always open: the user's memory lives there for everyone. */
  protected readonly hasSettings = computed(() => true);

  protected readonly enabledToolIds = signal<Set<string>>(new Set());

  protected readonly enabledCount = computed(() => this.enabledToolIds().size);

  constructor() {
    // Reset enabled tools when mcpServers change (e.g., agent switch)
    effect(() => {
      this.mcpServers();

      this.enabledToolIds.set(new Set());
      this.enabledToolsChange.emit([]);
    });
  }

  public focus(): void {
    this._textareaInput?.nativeElement?.focus();
  }

  public fillInput(text: string): void {
    const current = this.form.value.userInput ?? '';
    this.form.patchValue({ userInput: current + text });
  }

  protected isToolEnabled(id: string): boolean {
    return this.enabledToolIds().has(id);
  }

  protected toggleTool(id: string): void {
    this.enabledToolIds.update(set => {
      const updated = new Set(set);

      if (updated.has(id)) {
        updated.delete(id);
      } else {
        updated.add(id);
      }

      return updated;
    });

    this.enabledToolsChange.emit([...this.enabledToolIds()]);
  }

  protected toggleAllTools(): void {
    const servers = this.mcpServers();
    const allEnabled = this.enabledCount() === servers.length;

    if (allEnabled) {
      this.enabledToolIds.set(new Set());
    } else {
      this.enabledToolIds.set(new Set(servers.map(s => s.id)));
    }

    this.enabledToolsChange.emit([...this.enabledToolIds()]);
  }

  protected toggleDebug(): void {
    this.debugEnabled.update(v => !v);
    this.debugChange.emit(this.debugEnabled());
  }

  protected openSettings(): void {
    this.showToolsModal.update(v => !v);
  }

  protected closeToolsModal(): void {
    this.showToolsModal.set(false);
  }

  protected openMemory(): void {
    this.closeToolsModal();
    this.memoryRequested.emit();
  }

  protected sendMessage(): void {
    const files = this.selectedFiles();

    const hasFiles = files.length;
    const hasText = !!this.form.value.userInput;

    if (!hasFiles && !hasText) {
      return;
    }

    if (hasFiles) {
      this.filesSelected.emit(files);
      this.selectedFiles.set([]);
    }

    // Always emit messageSent so the parent can process files + text together
    this.messageSent.emit(this.form.value.userInput ?? '');

    this.form.reset();
  }

  protected onEnterKey(event: KeyboardEvent): void {
    if (!event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  protected onFileSelected(event: Event): void {
    const target = event.target as HTMLInputElement;

    this._addFiles(Array.from(target.files ?? []));

    // Reset input to allow selecting the same file again
    target.value = '';
  }

  /**
   * Pasting a file (e.g. a screenshot / image from the clipboard) attaches it
   * instead of dropping it into the text. A plain-text paste carries no files, so
   * it falls through to the normal paste handling untouched.
   */
  protected onPaste(event: ClipboardEvent): void {
    if (!this.enableUpload() || this.disabled()) {
      return;
    }

    const files = Array.from(event.clipboardData?.files ?? []);

    if (files.length === 0) {
      return;
    }

    event.preventDefault();
    this._addFiles(files);
  }

  protected onDragOver(event: DragEvent): void {
    if (!this.enableUpload() || this.disabled()) {
      return;
    }

    event.preventDefault();
    this._dragOver.set(true);
  }

  protected onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this._dragOver.set(false);
  }

  protected onDrop(event: DragEvent): void {
    if (!this.enableUpload() || this.disabled()) {
      return;
    }

    event.preventDefault();
    this._dragOver.set(false);
    this._addFiles(Array.from(event.dataTransfer?.files ?? []));
  }

  protected removeFile(index: number): void {
    this.selectedFiles.update(files => files.filter((_, i) => i !== index));
  }

  /** Append dropped/pasted/picked files to the pending attachments. */
  private _addFiles(files: File[]): void {
    if (files.length === 0) {
      return;
    }

    this.selectedFiles.update(current => [...current, ...files]);
  }
}
